import { useMemo } from "react";
import { useAuthStore } from "@utils/stores/authStore";
import { useBalance } from "@pages/Credits/hooks/useBalance";
import { useBalanceHistory } from "@pages/Credits/hooks/useBalanceHistory";
import { useMembers } from "@/utils/hooks/useMembers";
import type {
  UnifiedTeamCreditsData,
  EnrichedTeamMember,
  TeamMember,
} from "../types";

/**
 * Combined hook that fetches and merges team member data with credit information.
 *
 * This hook:
 * 1. Fetches balance data (business balance, allocated credits, team member credit info)
 * 2. Fetches team members (roles, status, contact info)
 * 3. Fetches transaction history
 * 4. Enriches team members with their credit information
 *
 * @param refreshTrigger - Boolean to trigger a refresh of team members data
 * @returns Unified data for team and credit management
 */
export const useUnifiedTeamCredits = (
  refreshTrigger: boolean = false
): UnifiedTeamCreditsData => {
  const { role } = useAuthStore();
  const isBusinessRole = role !== "client";

  // Fetch balance data (includes team member credit info)
  const {
    data: balanceData,
    isLoading: isBalanceLoading,
    error: balanceError,
    refetch: refetchBalance,
  } = useBalance(isBusinessRole);

  // Fetch team members (includes role, status, etc.)
  const {
    membersList,
    membersCount,
    setMembersList,
    loading: isMembersLoading,
    error: membersError,
  } = useMembers(refreshTrigger, role || null);

  // Fetch transaction history (owner only)
  const {
    data: transactionHistory,
    isLoading: isHistoryLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useBalanceHistory(1, 1000, role);

  // Enrich team members with credit data
  const enrichedMembers: EnrichedTeamMember[] = useMemo(() => {
    if (!membersList || !Array.isArray(membersList)) {
      return [];
    }

    return membersList.map((member: TeamMember) => {
      // Find credit info for this member
      const creditInfo = balanceData?.teamMembers?.find(
        (tm) => tm.userId === member.userId
      );

      return {
        ...member,
        allocatedCredits: creditInfo?.allocatedCredits ?? 0,
        businessUse: creditInfo?.businessUse ?? false,
        creditRequest: creditInfo?.creditRequest ?? false,
      };
    });
  }, [membersList, balanceData]);

  // Combine loading states
  const isLoading = isBalanceLoading || isMembersLoading || isHistoryLoading;

  // Combine errors
  const combinedError =
    (balanceError as Error) ||
    (membersError ? new Error(membersError) : null) ||
    (historyError as Error) ||
    null;

  // Refetch members wrapper
  const refetchMembers = () => {
    // The useMembers hook doesn't expose a refetch, but we can work around this
    // by using the setMembersList and triggering a refresh via refreshTrigger in parent
    // For now, we'll just return a no-op since the parent component can handle this
  };

  return {
    // Balance data
    businessBalance: balanceData?.businessBalance,
    userAllocatedCredits: balanceData?.allocatedCredits,
    hasCreditRequest: balanceData?.creditRequest,
    signupSource: balanceData?.signupSource,

    // Team data
    members: enrichedMembers,
    membersCount,
    setMembersList,

    // Transaction history
    transactionHistory: transactionHistory || [],

    // Loading states
    isLoading,
    isBalanceLoading,
    isMembersLoading,
    isHistoryLoading,

    // Error states
    error: combinedError,
    balanceError: balanceError as Error | null,
    membersError: membersError ? new Error(membersError) : null,
    historyError: historyError as Error | null,

    // Refresh functions
    refetchBalance,
    refetchMembers,
    refetchHistory,
  };
};
